const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export interface OpeningDay {
  weekday: number;
  opensAt: string | null;
  closesAt: string | null;
}

/** "9 am", "8:30 am" — how a shop writes a time, from "09:00". */
function clockTime(value: string): string {
  const [hour, minute] = value.split(':').map(Number);
  const suffix = (hour ?? 0) < 12 ? 'am' : 'pm';
  const shown = (hour ?? 0) % 12 === 0 ? 12 : (hour ?? 0) % 12;
  return minute ? `${shown}:${String(minute).padStart(2, '0')} ${suffix}` : `${shown} ${suffix}`;
}

/** Monday to Friday · Saturday and Sunday · Sunday — "to" reads badly for two. */
function formatRun(run: { from: number; to: number; label: string }, span: number): string {
  const days =
    span === 1
      ? WEEKDAYS[run.from]
      : span === 2
        ? `${WEEKDAYS[run.from]} and ${WEEKDAYS[run.to]}`
        : `${WEEKDAYS[run.from]} to ${WEEKDAYS[run.to]}`;
  return `${days} · ${run.label}`;
}

/**
 * The shop's week, as a person would write it on a door.
 *
 * Days that share their hours are collapsed into a run — "Monday to Friday",
 * not five identical lines, which is how a sign stops being read. The week
 * starts on Monday because the shop's week does, even though the data is
 * indexed from Sunday.
 *
 * Derived from Settings rather than typed anywhere, so a shop that starts
 * opening on Saturdays says so the moment somebody changes it.
 */
export function openingSummary(hours: OpeningDay[]): string[] {
  const week = [1, 2, 3, 4, 5, 6, 0].map((weekday) => hours.find((day) => day.weekday === weekday));
  const lines: string[] = [];
  let run: { from: number; to: number; label: string; span: number } | null = null;

  for (const day of week) {
    if (!day) continue;
    const label = day.opensAt && day.closesAt ? `${clockTime(day.opensAt)} – ${clockTime(day.closesAt)}` : 'Closed';
    if (run && run.label === label) {
      run.to = day.weekday;
      run.span += 1;
    } else {
      if (run) lines.push(formatRun(run, run.span));
      run = { from: day.weekday, to: day.weekday, label, span: 1 };
    }
  }
  if (run) lines.push(formatRun(run, run.span));

  return lines;
}
